import "../AuthStyle.css";
import { Form, Input, Button, Dropdown, Row, Col, Result, Spin } from "antd";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../context/AppContextProvider";
import { getTenant, userLogin } from "../../../network/network";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserType } from "../../../configs/constants";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { DownOutlined } from "@ant-design/icons";
import { handleApiError } from "../../../utils/errorHandler";
import { AUTH_ERROR_STORAGE_KEY } from "../../../configs/constants";
import { isTenantHost } from "../../../network/tenant";
import AccountChooser from "../../../components/AccountChooser/AccountChooser";

const Login = () => {
  const { appConfig, setAppConfigWrapper } = useContext(AppContext);
  const { setAuthUser } = useContext(AppContext);
  const [loading, setloading] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(() => isTenantHost());
  const [tenant, setTenant] = useState(null);
  const [tenantNotFound, setTenantNotFound] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loginValues, setLoginValues] = useState(null);
  let navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isTenantHost()) return;

    let active = true;

    getTenant()
      .then((response) => {
        if (active) setTenant(response?.data?.data || null);
      })
      .catch((error) => {
        if (active && error?.response?.status === 404) {
          setTenantNotFound(true);
        }
      })
      .finally(() => {
        if (active) setTenantLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let storedError;

    try {
      storedError = sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);
      sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
    } catch {
      return;
    }

    if (!storedError) return;

    try {
      handleApiError({ data: JSON.parse(storedError) }, t);
    } catch {
      handleApiError({ data: { message: storedError } }, t);
    }
  }, [t]);

  const showInvalidCredentials = () => {
    handleApiError({ data: { message: t("invalid_credentials") } }, t);
  };

  const processLogin = async (values) => {
    const response = await userLogin(values);

    if (response?.data?.success === false) {
      handleApiError(response, t);
      return;
    }

    const responseData = response?.data?.data;

    if (responseData?.selection_required) {
      const merchantAccounts = (responseData.accounts || []).filter(
        (account) =>
          Number(account.type) === UserType.BusinessOwner ||
          Number(account.type) === UserType.Employee,
      );

      if (merchantAccounts.length === 0 || values.company_id) {
        showInvalidCredentials();
        return;
      }

      if (merchantAccounts.length === 1) {
        await processLogin({
          ...values,
          company_id: merchantAccounts[0].company_id,
        });
        return;
      }

      setLoginValues(values);
      setAccounts(merchantAccounts);
      return;
    }

    if (
      response?.data?.success &&
      (Number(responseData?.user?.type) === UserType.BusinessOwner ||
        Number(responseData?.user?.type) === UserType.Employee)
    ) {
      localStorage.setItem("access_token", responseData.user.access_token);
      setAuthUser(responseData.user);
      navigate("/home");
      return;
    }

    showInvalidCredentials();
  };

  const onFinish = async (values) => {
    try {
      setloading(true);
      await processLogin(values);
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setloading(false);
    }
  };

  const handleAccountSelect = async (account) => {
    try {
      setloading(true);
      await processLogin({
        ...loginValues,
        company_id: account.company_id,
      });
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setloading(false);
    }
  };

  const baseDomain = (process.env.REACT_APP_TENANT_BASE_DOMAIN || "")
    .trim()
    .replace(/^\.+|\.+$/g, "");
  const genericPortalUrl = `https://portal.${baseDomain}`;
  const tenantName =
    appConfig.direction === "rtl" && tenant?.business_arabic_name
      ? tenant.business_arabic_name
      : tenant?.name;

  const LangOptions = [
    {
      key: "1",
      label: (
        <div
          onClick={() => {
            setAppConfigWrapper({ direction: "ltr" });
          }}
        >
          English
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          onClick={() => {
            setAppConfigWrapper({ direction: "rtl" });
          }}
        >
          عربي
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        backgroundImage: "url('/images/bg-img.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "top center",
        boxShadow: "inset 0 0 0 2000px var(--color-shadow-soft)",
        minHeight: "100vh",
        direction: appConfig.direction
          ? appConfig.direction === "ltr"
            ? "ltr"
            : "rtl"
          : "ltr",
      }}
    >
      <div className="main-wrapper">
        <header className="header">
          <div className="app-flex-between">
            <div className="app-flex">
              <a
                href="https://anypos.ai/"
                target="_blank"
                rel="noreferrer"
                className="app-flex"
              >
                <img
                  src="/anypos-logo.svg"
                  alt="anypos"
                  className="main_logo"
                />
              </a>
            </div>
            <div className="Button-area app-flex-align-center-gap-10">
              <a href="https://anypos.ai/" target="_blank" rel="noreferrer">
                <Button type="default" className="md_down_display_none">
                  {t("visit_website")}
                </Button>
              </a>

              <Link to="/register">
                <Button type="primary">{t("sign_up")}</Button>
              </Link>

              <Dropdown
                trigger={["click"]}
                menu={{
                  items: LangOptions,
                }}
                className="md_down_display_none"
              >
                <Button type="default">
                  {appConfig.direction
                    ? appConfig.direction === "ltr"
                      ? "English"
                      : "عربي"
                    : "English"}
                  <span
                    style={{
                      margin: appConfig.direction
                        ? appConfig.direction === "ltr"
                          ? "0 0 0 8px"
                          : "0 8px 0 0"
                        : "0 0 0 8px",
                    }}
                  >
                    <DownOutlined className="app-text-xs" />
                  </span>
                </Button>
              </Dropdown>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: LangOptions,
                }}
                className="md_up_display_none"
              >
                <img
                  src="/icons/globe.png"
                  alt="language"
                  style={{
                    width: "fit-content",
                    height: "fit-content",
                    backgroundColor: "var(--color-primary)",
                    borderRadius: "5px",
                    padding: "2px",
                  }}
                />
              </Dropdown>
            </div>
          </div>
        </header>
        <div className="auth_area">
          <Row>
            <Col xs={24} sm={3} md={4} lg={6} xl={7} xxl={8}></Col>
            <Col xs={24} sm={18} md={16} lg={12} xl={10} xxl={8}>
              <Spin spinning={loading || tenantLoading}>
                <div
                  style={{
                    backgroundColor: "var(--color-white-soft)",
                    padding: "20px",
                    boxShadow: "0px 0px 10px var(--color-overlay)",
                    borderRadius: "20px",
                  }}
                >
                  {tenantNotFound ? (
                    <Result
                      status="404"
                      title={t("business_not_found")}
                      subTitle={t("business_not_found_description")}
                      extra={
                        <Button type="primary" href={genericPortalUrl}>
                          {t("go_to_generic_portal")}
                        </Button>
                      }
                    />
                  ) : accounts.length > 1 ? (
                    <AccountChooser
                      accounts={accounts}
                      onSelect={handleAccountSelect}
                      loading={loading}
                    />
                  ) : (
                    <Form
                      name="login-user-form"
                      layout="vertical"
                      onFinish={onFinish}
                    >
                      {tenant ? (
                        <div style={{ textAlign: "center", marginBottom: 20 }}>
                          {tenant.logo ? (
                            <img
                              src={tenant.logo}
                              alt={tenantName || t("business_logo")}
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                              style={{ maxWidth: 120, maxHeight: 80 }}
                            />
                          ) : null}
                          {tenantName ? <h2>{tenantName}</h2> : null}
                        </div>
                      ) : null}
                      <div className="app-auth-form-footer">
                        <h2>{t("login")}</h2>
                      </div>

                      <Form.Item
                        name="phone"
                        label={t("phone_number") + " eg: +966 5xxxxxxx"}
                        rules={[
                          {
                            required: true,
                            message: t("validate_phone_required"),
                          },
                          {
                            min: 12,
                            message: t("validate_phone_exact_limit"),
                          },
                          {
                            max: 12,
                            message: t("validate_phone_exact_limit"),
                          },
                        ]}
                      >
                        <PhoneInput
                          localization={["sa"]}
                          country={"sa"}
                          onlyCountries={["sa"]}
                          masks={{ sa: "(...) ..-..-.." }}
                          placeholder="+966 (123) 45-67-89"
                          style={{
                            direction: "ltr",
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label={t("password")}
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t("validate_password_required"),
                          },
                        ]}
                      >
                        <Input.Password size="large" />
                      </Form.Item>

                      <div
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          display: "flex",
                          margin: "20px 0 10px 0",
                        }}
                      >
                        <Link to="/forgot">
                          <Button type="default">{t("forgot")}</Button>
                        </Link>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          {t("login")}
                        </Button>
                      </div>
                    </Form>
                  )}
                </div>
              </Spin>
            </Col>
            <Col xs={24} sm={3} md={4} lg={6} xl={7} xxl={8}></Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Login;
